/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Star, Save, Trash2, Sparkles, BookmarkPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { listRxTemplates, createRxTemplate, deleteRxTemplate, bumpRxTemplateUsage } from '@/lib/rxTemplates';

/**
 * RxTemplatePicker — UI pill (button + dropdown) that lets the doctor:
 *   - Load a saved prescription template into the form.
 *   - Save the current form contents as a new template.
 *   - Delete templates they no longer use.
 *
 * Props:
 *   currentForm  — { specialty, diagnosis, drugs: [{name, aic, quantity, posology, notes}] }
 *   onLoad(tpl)  — called when the user picks a template. Receives the full template doc.
 */
export const RxTemplatePicker = ({ currentForm, onLoad }) => {
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');

  const load = async () => {
    try {
      const { items } = await listRxTemplates();
      setTemplates(items || []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const canSave = useMemo(() => {
    const drugs = (currentForm?.drugs || []).filter((d) => d?.name?.trim());
    return drugs.length > 0;
  }, [currentForm]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Dai un nome al template');
      return;
    }
    if (!canSave) {
      toast.error('Aggiungi almeno un farmaco prima di salvare');
      return;
    }
    try {
      const payload = {
        name: name.trim(),
        specialty: currentForm?.specialty || null,
        diagnosis: currentForm?.diagnosis || null,
        drugs: (currentForm?.drugs || []).filter((d) => d?.name?.trim()).map((d) => ({
          name: d.name,
          aic: d.aic || null,
          quantity: d.quantity || null,
          posology: d.posology || null,
          notes: d.notes || null,
          active: d.active || null,
        })),
        notes: currentForm?.notes || null,
      };
      await createRxTemplate(payload);
      toast.success(`Template “${name}” salvato`);
      setName('');
      setSaveOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleLoad = async (tpl) => {
    try {
      await bumpRxTemplateUsage(tpl.id);
    } catch { /* ignore */ }
    if (typeof onLoad === 'function') onLoad(tpl);
    toast.info(`Template “${tpl.name}” caricato`);
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Eliminare il template “${tpl.name}”?`)) return;
    try {
      await deleteRxTemplate(tpl.id);
      toast.success('Template eliminato');
      await load();
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="rx-template-picker">
      {templates.slice(0, 4).map((tpl) => (
        <Button
          key={tpl.id}
          size="sm"
          variant="outline"
          onClick={() => handleLoad(tpl)}
          data-testid={`rx-tpl-load-${tpl.id}`}
          title={`${tpl.drugs?.length || 0} farmaci — usato ${tpl.usage_count || 0} volte`}
        >
          <Sparkles className="h-3.5 w-3.5 mr-1.5 text-primary" />
          {tpl.name}
          {tpl.usage_count > 0 && (
            <Badge variant="secondary" className="ml-1.5 h-4 px-1.5 text-[10px]">
              {tpl.usage_count}
            </Badge>
          )}
        </Button>
      ))}

      {templates.length > 4 && (
        <Dialog>
          <DialogTrigger asChild>
            <Button size="sm" variant="ghost" data-testid="rx-tpl-more-btn">
              +{templates.length - 4} preferiti
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Tutti i template ricetta</DialogTitle></DialogHeader>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {templates.map((tpl) => (
                <Card key={tpl.id}>
                  <CardContent className="py-3 flex items-center gap-3">
                    <Star className="h-4 w-4 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{tpl.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {(tpl.drugs || []).map((d) => d.name).join(' • ')}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{tpl.usage_count || 0} usi</Badge>
                    <Button size="sm" onClick={() => handleLoad(tpl)}>Carica</Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(tpl)} aria-label="Elimina">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {!templates.length && (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Nessun template salvato. Compila una ricetta e clicca “Salva come template”.
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline" disabled={!canSave} data-testid="rx-tpl-save-btn">
            <BookmarkPlus className="h-3.5 w-3.5 mr-1" /> Salva come template
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Salva template ricetta</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Nome descrittivo</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Antibiotico + gastroprotettore"
              autoFocus
              data-testid="rx-tpl-name-input"
            />
            <p className="text-xs text-muted-foreground">
              Verranno salvati {(currentForm?.drugs || []).filter(d => d?.name?.trim()).length} farmaci.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} data-testid="rx-tpl-save-confirm"><Save className="h-4 w-4 mr-1" /> Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RxTemplatePicker;
